import { Box3, Color, ColorManagement, PCFSoftShadowMap, SRGBColorSpace, WebGLRenderer } from 'three';

import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { GTAOPass } from 'three/addons/postprocessing/GTAOPass.js';
import { SSAARenderPass } from 'three/addons/postprocessing/SSAARenderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';

import WebGL from 'three/addons/capabilities/WebGL.js';
import { RenderLoop } from './renderLoop';
import { BaseClass } from 'dac-web-base';
import { Debug } from './utils/debug';

export class RendererUnit extends BaseClass {
	constructor() {
		super();
		this.renderer = null;
		this.composer = null;
		this.renderMethods = [];
		this.fps = 30;
	}

	init( container ) {

		if( !container ) return;
		this.container = container;

		// init renderer
		if ( WebGL.isWebGL2Available() ) this.#initWebGL2();
		else this.#initWebGL();

		// init XR
		//this.#initXR();

		// init shadow map
		this.renderer.shadowMap.enabled = true;
		this.renderer.shadowMap.type = PCFSoftShadowMap;

		// init color space
		ColorManagement.enabled = true;
		this.renderer.outputColorSpace = SRGBColorSpace;

		// init size
		const size = this.container.getBoundingClientRect();
		this.renderer.setSize( size.width, size.height );
		window.addEventListener( 'resize', () => this.#onWindowResize( this.container.clientWidth, this.container.clientHeight ) );

		// add to dom
		this.container.appendChild( this.renderer.domElement ); 

		//set background color
		this.renderer.setClearColor( new Color( 0xb0babf ), 1 );

		// fill these values before rendering
		this.scene = null;
		this.camera = null;
		this.controls = null;
		
		this._loop = new RenderLoop( this );

		Debug.Instance.setRenderer( this.renderer );
	}

	render( deltaTime ) {
		if( !this.scene ) return;
		
		if( this.composer ) 
			this.composer.render( deltaTime );
		else 
			this.renderer.render( this.scene, this.camera );

		this.renderMethods.forEach( method => method() );

		if( this.controls ) this.controls.update();
	}

	#initWebGL() {
		this.renderer = new WebGLRenderer({
			alpha: true,
			preserveDrawingBuffer: true,
			antialias: true,
			powerPreference: 'high-performance',
			stencil: true
		});
	}

	#initWebGL2() {
		const canvas = document.createElement('canvas');
		
		const context = canvas.getContext('webgl2', {
			alpha: false,
			antialias: true,
			stencil: true,
			preserveDrawingBuffer: true,
		});
		this.renderer = new WebGLRenderer({
			antialias: true,
			preserveDrawingBuffer: true,
			powerPreference: 'high-performance',
			canvas: canvas,
			context: context
		});
	}

	initComposer( scene, camera ) {
		this.composer = new EffectComposer( this.renderer );
		this.composer.addPass( new RenderPass( scene, camera ) );
		
		const gtaoPass = new GTAOPass( scene, camera );
		const box = new Box3().setFromObject( scene );
		gtaoPass.setSceneClipBox( box );
		this.composer.addPass( gtaoPass );
		this.composer.addPass( new SSAARenderPass( scene, camera ) );

		this.composer.addPass( new OutputPass() );
	}

	clearComposer() {
		this.composer = null;
	}

	#onWindowResize( width, height ) {
		this.renderer?.setSize( width, height );
		this.composer?.setSize( width, height );
		this.trigger( 'onResize', { width: width, height: height } );
	}

	dispose() {
		this.renderer?.dispose();
		this.container?.removeChild( this.renderer.domElement );
	}

	doContinuousRender( continuos ) {
		this._loop.doContinuousRender( continuos );
	}

}